import { useMemo } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { isEmpty, isNil, upperFirst } from 'lodash';
import { COLUMN_TYPES } from '../../../data-table/DataTableColumns';
import useRecordContext from '../../../../utilities/RecordContextProviderUtilities';
import InfrastructureTable from '../BaseInfrastructureTable/index';
import { MONITORED_INFRASTRUCTURE_DATA_SOURCE } from '../InfrastructureConstants';
import { ACCESS_POINTS_URL_SUFFIX } from './DeviceConstants';

const EMPTY_STATE_MESSAGE = 'No access point information available for this device';
const TABLE_ID = 'DeviceAccessPoint';

/**
 * Formats the connect status to a readable label
 * @param {string} value - Raw connect status string
 * @returns {string} Capitalized status or '--' if empty/nil
 */
const formatConnectStatus = (value) => (isNil(value) || isEmpty(value) ? '--' : upperFirst(value));

/**
 * Read-only table displaying associated access points for wireless controller devices
 * Uses InfrastructureTable compound components with selector container
 * @returns {JSX.Element} InfrastructureTable configured for access point data
 */
const DeviceAccessPointTable = () => {
    const { id: deviceId } = useRecordContext();

    const fetchUrl = `${MONITORED_INFRASTRUCTURE_DATA_SOURCE.crudUrl}/${ACCESS_POINTS_URL_SUFFIX}`;
    const fetchParams = useMemo(() => ({ wlc: deviceId }), [deviceId]);

    const columns = useMemo(
        () => [
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Name`),
                mapping: 'name',
                width: 150,
                sortable: true,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Model`),
                mapping: 'deviceModel',
                width: 150,
                sortable: true,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Status`),
                mapping: 'connectStatus',
                width: 100,
                sortable: true,
                formatter: formatConnectStatus,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`IP Address`),
                mapping: 'ipAddress',
                width: 100,
                sortable: true,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`MAC Address`),
                mapping: 'macAddress',
                width: 150,
                sortable: true,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Serial Number`),
                mapping: 'serialNumber',
                width: 150,
                sortable: true,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Location`),
                mapping: 'location',
                width: 150,
                sortable: true,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Description`),
                mapping: 'description',
                flex: 1,
                sortable: true,
            },
        ],
        []
    );

    return (
        <InfrastructureTable
            title={<Trans>Access Points</Trans>}
            tableId={TABLE_ID}
            emptyStateMessage={EMPTY_STATE_MESSAGE}
            fetchUrl={fetchUrl}
            fetchParams={fetchParams}
            columns={columns}
        >
            <InfrastructureTable.SelectorContainer>
                <InfrastructureTable.Content />
            </InfrastructureTable.SelectorContainer>
        </InfrastructureTable>
    );
};

export default DeviceAccessPointTable;
